import { ECommerceOnboarding, ECommerceOnboardingStep } from "consts/ecommerce";
import { withDetectIncomplete } from "features/ecommerce/helpers";
import type { HighRiskMerchant } from "features/ecommerce/types";
import useApplication from "features/ecommerce/useOnboardingApplication";
import { useOnboardingApplication } from "ui/organizms/ECommerce/Onboarding/common";

const useOnboardingHighRiskApplication = () => {
    const { response } = useApplication<HighRiskMerchant.OnboardingApplication>();

    const getApplicationModel = () => {
        const {
            isIncompleteClientBusinessInformationStep,
            isIncompleteClientDirectorsShareholdersStep,
            isIncompleteClientMarketingSupportStep,
            isIncompleteClientPaymentMethodsStep,
            isIncompleteClientRiskComplianceStep,
            isIncompleteClientDocumentStep,
            isIncompleteClientFinancialInformationStep,

            hasRequestedChangeClientBusinessInformationStep = false,
            hasRequestedChangeClientDirectorsShareholdersStep = false,
            hasRequestedChangeClientPaymentMethodsStep = false,
            hasRequestedChangeClientMarketingSupportStep = false,
            hasRequestedChangeClientRiskComplianceStep = false,
            hasRequestedChangeClientDocumentStep = false,
            hasRequestedChangeClientFinancialInformationStep = false,

            hasAgreedTermsConditions = false
        } = response.data ?? {};

        return new Map<number, {
            readonly index: number;
            readonly isIncomplete: boolean;
            readonly hasRequestedChange: boolean;
            readonly countable?: boolean;
            readonly layout?: {
                readonly stepContainerSize: string | number;
            }
        }>()
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].GettingStarted, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].GettingStarted,
                isIncomplete: !response.data,
                hasRequestedChange: false,
                countable: false
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BusinessInformation, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BusinessInformation,
                isIncomplete: withDetectIncomplete(isIncompleteClientBusinessInformationStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientBusinessInformationStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].DirectorsAndShareholders, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].DirectorsAndShareholders,
                isIncomplete: withDetectIncomplete(isIncompleteClientDirectorsShareholdersStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientDirectorsShareholdersStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].FinancialInformation, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].FinancialInformation,
                isIncomplete: withDetectIncomplete(isIncompleteClientFinancialInformationStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientFinancialInformationStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].PaymentMethods, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].PaymentMethods,
                isIncomplete: withDetectIncomplete(isIncompleteClientPaymentMethodsStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientPaymentMethodsStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].RiskAndCompliance, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].RiskAndCompliance,
                isIncomplete: withDetectIncomplete(isIncompleteClientRiskComplianceStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientRiskComplianceStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].MarketingWebsiteAndSupport, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].MarketingWebsiteAndSupport,
                isIncomplete: withDetectIncomplete(isIncompleteClientMarketingSupportStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientMarketingSupportStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].UploadDocuments, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].UploadDocuments,
                isIncomplete: withDetectIncomplete(isIncompleteClientDocumentStep),
                hasRequestedChange: Boolean(hasRequestedChangeClientDocumentStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].CompleteOnboarding, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.HighRiskMerchant].CompleteOnboarding,
                isIncomplete: !hasAgreedTermsConditions,
                hasRequestedChange: false,
                countable: false
            });
    };

    return {
        ...useOnboardingApplication({
            getApplicationModel
        }),
        getApplicationModel
    };
};

export {
    useOnboardingHighRiskApplication as useOnboardingApplication
};
